/**
 * Pre-defined Layout Configurations
 *
 * Test layouts for each lifecycle stage + overlay combination
 */

import {
  LayoutConfig,
  LifecycleStage,
  DashboardOverlay,
  ModuleId,
} from './types';

export const LAYOUT_CONFIGS: Record<string, LayoutConfig> = {
  // 1. Brand new user
  new: {
    name: 'New User',
    description: 'No sources, no transactions',
    stage: LifecycleStage.NEW,
    modules: [
      { id: ModuleId.OnboardingChecklist, score: 100 },
      { id: ModuleId.ConnectedExchanges, score: 35 },
      { id: ModuleId.RecentActivity, score: 30 },
      { id: ModuleId.TaxDashboard, score: 25 },
      { id: ModuleId.TopHoldings, score: 22 },
      { id: ModuleId.BridgeReview, score: 21 },
      { id: ModuleId.Tips, score: 20 },
      { id: ModuleId.UpgradeCTA, score: 15 },
      { id: ModuleId.AppDownload, score: 10 },
    ],
  },

  // 2. First sync in progress
  connecting: {
    name: 'Connecting',
    description: 'Source added, first sync running',
    stage: LifecycleStage.CONNECTING,
    modules: [
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.ConnectedExchanges, score: 60 },
      { id: ModuleId.Tips, score: 15 },
      { id: ModuleId.AppDownload, score: 10 },
    ],
  },

  // 3. Has data, no tax settings
  bootstrapped: {
    name: 'Bootstrapped',
    description: 'Some txns, no tax settings',
    stage: LifecycleStage.BOOTSTRAPPED,
    modules: [
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.TaxDashboard, score: 46 },
      { id: ModuleId.RecentActivity, score: 18 },
      { id: ModuleId.ConnectedExchanges, score: 20 },
      { id: ModuleId.AppDownload, score: 10 },
    ],
  },

  // 4. Active light portfolio
  activeLight: {
    name: 'Active Light',
    description: '<500 txns, low issues',
    stage: LifecycleStage.ACTIVE_LIGHT,
    modules: [
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.RecentActivity, score: 18 },
      { id: ModuleId.TaxDashboard, score: 16 },
      { id: ModuleId.ConnectedExchanges, score: 20 },
      { id: ModuleId.TopHoldings, score: 15 },
      { id: ModuleId.RecentTaxDisposals, score: 14 },
      { id: ModuleId.AppDownload, score: 10 },
      { id: ModuleId.UpgradeCTA, score: 8 },
    ],
  },

  // 5. Active heavy DeFi
  activeHeavy: {
    name: 'Active Heavy',
    description: '≥500 txns or ≥5 chains',
    stage: LifecycleStage.ACTIVE_HEAVY,
    modules: [
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.BridgeReview, score: 15 },
      { id: ModuleId.TaxTasks, score: 12 },
      { id: ModuleId.RecentActivity, score: 18 },
      { id: ModuleId.TaxDashboard, score: 16 },
      { id: ModuleId.ConnectedExchanges, score: 20 },
      { id: ModuleId.AppDownload, score: 10 },
    ],
  },

  // 6. Filing season
  filingMode: {
    name: 'Filing Mode',
    description: 'Within 30 days of deadline',
    stage: LifecycleStage.FILING_MODE,
    modules: [
      { id: ModuleId.FilingCountdown, score: 110 },
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.TaxTasks, score: 52 },
      { id: ModuleId.TaxDashboard, score: 41 },
      { id: ModuleId.RecentTaxDisposals, score: 14 },
      { id: ModuleId.AppDownload, score: 10 },
    ],
  },

  // 7. Power user
  powerUser: {
    name: 'Power User',
    description: 'Compact mode, high density',
    stage: LifecycleStage.POWER_USER,
    modules: [
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.RecentActivity, score: 28 },
      { id: ModuleId.TaxDashboard, score: 16 },
      { id: ModuleId.TopHoldings, score: 15 },
      { id: ModuleId.AppDownload, score: 10 },
    ],
  },

  // 8. Broken connections overlay
  brokenConnections: {
    name: 'Broken Connections',
    description: 'Failed API keys',
    stage: LifecycleStage.ACTIVE_LIGHT,
    overlays: [DashboardOverlay.BROKEN_CONNECTIONS],
    modules: [
      { id: ModuleId.AlertsCenter, score: 140 },
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.ConnectedExchanges, score: 50 },
      { id: ModuleId.RecentActivity, score: 18 },
    ],
  },

  // 9. Bridge review needed
  bridgeReview: {
    name: 'Bridge Review',
    description: 'Unmatched bridge transfers',
    stage: LifecycleStage.ACTIVE_HEAVY,
    overlays: [DashboardOverlay.BRIDGE_REVIEW_NEEDED],
    modules: [
      { id: ModuleId.BridgeReview, score: 95 },
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.TaxTasks, score: 12 },
      { id: ModuleId.RecentActivity, score: 18 },
    ],
  },

  // 10. Audit flags
  auditFlags: {
    name: 'Audit Flags',
    description: 'Missing basis, wash sales',
    stage: LifecycleStage.ACTIVE_LIGHT,
    overlays: [DashboardOverlay.AUDIT_FLAGS],
    modules: [
      { id: ModuleId.AlertsCenter, score: 120 },
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.RecentActivity, score: 18 },
      { id: ModuleId.TaxDashboard, score: 16 },
    ],
  },

  // 11. Multiple overlays (worst case)
  multipleIssues: {
    name: 'Multiple Issues',
    description: 'Broken keys + bridge + audit',
    stage: LifecycleStage.ACTIVE_HEAVY,
    overlays: [
      DashboardOverlay.BROKEN_CONNECTIONS,
      DashboardOverlay.BRIDGE_REVIEW_NEEDED,
      DashboardOverlay.AUDIT_FLAGS,
    ],
    modules: [
      { id: ModuleId.AlertsCenter, score: 200 },
      { id: ModuleId.ConnectedExchanges, score: 100 },
      { id: ModuleId.BridgeReview, score: 95 },
      { id: ModuleId.HeroOverview, score: 50 },
      { id: ModuleId.TaxTasks, score: 12 },
    ],
  },
};

/**
 * Get all available layout options for selector
 */
export function getLayoutOptions(): Array<{ value: string; label: string; description: string }> {
  return Object.entries(LAYOUT_CONFIGS).map(([key, config]) => ({
    value: key,
    label: config.name,
    description: config.description,
  }));
}
